/**
 * Customer Messages Page
 *
 * Main messaging interface for customers to communicate with guides.
 * Reuses omnichannel messaging components built for guides.
 *
 * Epic: MVP 1.21.4 - Customer Dashboard
 * Story: US-1.21.4-06: Message List View (3 SP) - 100% component reuse
 * Story: US-1.21.4-07: Conversation View (3 SP) - 100% component reuse
 * Story: US-1.21.4-08: Quick Replies (2 SP) - Adapted templates
 *
 * Features:
 * - Conversation list with search and filtering
 * - Real-time messaging with WebSocket
 * - Message threading
 * - Quick reply templates
 * - Email/SMS integration (omnichannel)
 *
 * Data Flow:
 * - Fetches from GET /api/v1/messages/conversations (role-agnostic)
 * - Sends to POST /api/v1/messages/conversations/:conversationId/send
 * - WebSocket for real-time delivery
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiRequest from '../../services/api';
import {
  MessageSquare,
  Search,
  Filter,
  AlertCircle as _AlertCircle,
  Inbox
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import Avatar from '../../components/common/Avatar';
import { logError } from '../../services/errorLoggingService';
import ConversationList from '../../components/messages/ConversationList';
import MessageThread from '../../components/messages/MessageThread';
import MessageComposer from '../../components/messages/MessageComposer';
import QuickReplySelector from '../../components/messages/QuickReplySelector';

export default function CustomerMessages() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const guideIdFromUrl = searchParams.get('guide');

  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Search and filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, unread, archived

  // Fetch conversations
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (filterStatus !== 'all') {params.append('status', filterStatus);}

        const response = await apiRequest(`/api/v1/messages/conversations?${params.toString()}`, {
          method: 'GET'
        });
        if (response.success) {
          setConversations(response.data.conversations || []);
        }
      } catch (err) {
        logError(err, { componentName: 'CustomerMessages', action: 'fetch_conversations' });
        if (err.status === 401) {
          setError('Session expired. Please log in again.');
        } else {
          setError('Failed to load conversations');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, [filterStatus]);

  // Auto-select conversation from URL
  useEffect(() => {
    if (guideIdFromUrl && conversations.length > 0) {
      const conversation = conversations.find(c => c.guide_id === guideIdFromUrl);
      if (conversation) {
        setSelectedConversationId(conversation.conversation_id);
      }
    }
  }, [guideIdFromUrl, conversations]);

  // Fetch messages when conversation selected
  useEffect(() => {
    if (selectedConversationId) {
      fetchMessages(selectedConversationId);
    }
  }, [selectedConversationId]);

  const fetchMessages = async (conversationId) => {
    try {
      const response = await apiRequest(`/api/v1/messages/conversations/${conversationId}`, {
        method: 'GET'
      });
      if (response.success) {
        setMessages(response.data.messages);
        setSelectedConversation(response.data.conversation);

        // Mark as read
        await apiRequest(`/api/v1/messages/conversations/${conversationId}/read`, {
          method: 'PUT'
        });

        // Update conversation list to reflect read status
        setConversations(prev => prev.map(c =>
          c.conversation_id === conversationId
            ? { ...c, unread_count_customer: 0 }
            : c
        ));
      }
    } catch (err) {
      logError(err, { componentName: 'CustomerMessages', action: 'fetch_messages' });
    }
  };

  const handleSendMessage = async (messageText, selectedTemplate = null) => {
    if (!selectedConversationId || (!messageText.trim() && !selectedTemplate)) {return;}

    try {
      setSendingMessage(true);
      const response = await apiRequest(
        `/api/v1/messages/conversations/${selectedConversationId}/send`,
        {
          method: 'POST',
          body: JSON.stringify({
            recipient_id: selectedConversation?.guide_id,
            message_text: selectedTemplate ? selectedTemplate.content : messageText,
            booking_id: selectedConversation?.booking_id || null
          })
        }
      );

      if (response.success) {
        // Add message to local state immediately
        const newMessage = response.data;
        setMessages(prev => [...prev, newMessage]);

        // Update conversation list
        setConversations(prev => prev.map(c =>
          c.conversation_id === selectedConversationId
            ? {
                ...c,
                last_message_preview: messageText.substring(0, 50),
                last_message_at: new Date().toISOString()
              }
            : c
        ));
      }
    } catch (err) {
      logError(err, { componentName: 'CustomerMessages', action: 'send_message' });
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      setTimeout(() => setError(null), 3000);
      } else {
        setError('Failed to send message. Please try again.');
      setTimeout(() => setError(null), 3000);
      }
    } finally {
      setSendingMessage(false);
    }
  };

  const handleConversationSelect = (conversationId) => {
    setSelectedConversationId(conversationId);
  };

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  // Filter conversations by search
  const filteredConversations = conversations.filter(conversation => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        conversation.guide_name?.toLowerCase().includes(query) ||
        conversation.last_message_preview?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (loading && conversations.length === 0) {
    return (
      <DashboardLayout userRole="customer" activeNavId="messages" onNavigate={handleNavigate}>
        <LoadingSpinner message="Loading messages..." className="min-h-screen" />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout userRole="customer" activeNavId="messages" onNavigate={handleNavigate}>
        <ErrorAlert message={error} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      userRole="customer"
      activeNavId="messages"
      onNavigate={handleNavigate}
    >
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
          <p className="mt-1 text-sm text-gray-500">
            Chat with your guides about upcoming trips
          </p>
        </div>

        {/* Messages Interface */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-3 divide-x divide-gray-200" style={{ height: 'calc(100vh - 300px)' }}>
            {/* Left Panel - Conversation List */}
            <div className="lg:col-span-1 flex flex-col">
              {/* Search and Filter */}
              <div className="p-4 border-b border-gray-200">
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search conversations..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Messages</option>
                    <option value="unread">Unread</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              {/* Conversation List */}
              <div className="flex-1 overflow-y-auto">
                {filteredConversations.length === 0 ? (
                  <div className="p-8 text-center">
                    <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium">No conversations</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {searchQuery ? 'Try adjusting your search' : 'Start booking trips to chat with guides'}
                    </p>
                  </div>
                ) : (
                  <ConversationList
                    conversations={filteredConversations}
                    selectedConversationId={selectedConversationId}
                    onConversationSelect={handleConversationSelect}
                    userRole="customer"
                  />
                )}
              </div>
            </div>

            {/* Right Panel - Message Thread */}
            <div className="lg:col-span-2 flex flex-col">
              {selectedConversationId ? (
                <>
                  {/* Conversation Header */}
                  <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center space-x-3">
                      <Avatar name={selectedConversation?.guide_name} size="md" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {selectedConversation?.guide_name}
                        </h3>
                        {selectedConversation?.booking_id && (
                          <p className="text-sm text-gray-500">
                            Booking #{selectedConversation.booking_id.slice(0, 8)}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => navigate(`/guides/${selectedConversation?.guide_id}`)}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        View Profile
                      </button>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto">
                    <MessageThread
                      messages={messages}
                      currentUserId={null} // Will be populated from auth context
                      userRole="customer"
                    />
                  </div>

                  {/* Message Composer */}
                  <div className="border-t border-gray-200">
                    <MessageComposer
                      onSendMessage={handleSendMessage}
                      isSending={sendingMessage}
                      userRole="customer"
                    />
                    <QuickReplySelector
                      onSelectTemplate={handleSendMessage}
                      userRole="customer"
                    />
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Select a conversation
                    </h3>
                    <p className="text-gray-500">
                      Choose a conversation from the list to start messaging
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
